/* eslint-disable react/no-unescaped-entities */
/**
 * She Is™ — 12-Week Arrival & Embodiment Interactive Experience.
 * Route: /coaching/she-is/app
 *
 * Intentionally a different visual language from Me I See (light violet)
 * and HWYCF (near-black surgery). She Is is bright, gradient-heavy,
 * horizontal (not sidebar), and green-forward — this is the arrival, not
 * the excavation. Design tokens locked to the prototype's palette:
 *   purple #5B2C83 · purple-bold #6E37A0 · purple-deep #3D1D5C
 *   green  #7FA66A · green-bold  #6C9459 · green-soft  #EAF2E6
 *
 * Week 12 is a Declaration week — no worksheet/journal/activity, instead
 * 4 sentence-starters + editable affirmation list. Completing Week 12
 * flips program_completed and unlocks the 3-stage Arrival Sequence:
 *   Stage 0: She Is Declaration Certificate
 *   Stage 1: Letter From The Woman You've Become (Claude 4.5)
 *   Stage 2: She Is Society Invitation
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Check, LogOut, ChevronRight, Loader2, Printer, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "she_is_token";
const EMAIL_KEY = "she_is_email";
const NAME_KEY = "she_is_name";
const PROGRAM_SLUG = "she-is";

const C = {
  purple: "#5B2C83",
  purpleBold: "#6E37A0",
  purpleDeep: "#3D1D5C",
  white: "#FFFFFF",
  black: "#1B1720",
  silver: "#C0C0C0",
  green: "#7FA66A",
  greenBold: "#6C9459",
  greenSoft: "#EAF2E6",
  textDim: "#6B6472",
  textFaint: "#A69FB0",
  border: "#ECE5F2",
  bg: "#FFFFFF",
  shadow: "0 20px 50px rgba(91,44,131,0.14)",
};

const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t
    ? { Authorization: `Bearer ${t}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
};
const api = {
  async get(p) {
    const r = await fetch(`${API}${p}`, { headers: authHeaders() });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  },
  async patch(p, body) {
    const r = await fetch(`${API}${p}`, { method: "PATCH", headers: authHeaders(), body: JSON.stringify(body) });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  },
  async post(p, body) {
    const r = await fetch(`${API}${p}`, { method: "POST", headers: authHeaders(), body: JSON.stringify(body || {}) });
    if (!r.ok) throw new Error(String(r.status));
    return r.json();
  },
};

const WEEKLY_SUBTITLES = {
  1: "Who is this new woman? How has she changed? What no longer belongs?",
  2: "Not because she earned it. Not because she proved herself. Simply because she exists.",
  3: "She stops trying to complete herself. She discovers she already is.",
  4: "Freedom from shame, guilt, comparison, perfectionism, and people pleasing.",
  5: "Confidence without arrogance. Confidence without performance. Confidence rooted in identity.",
  6: "No longer hiding. No longer shrinking. No longer apologizing.",
  7: "Enough before success. Enough before relationships. Enough before achievement.",
  8: "How does she serve? Why was she created? What impact does she leave?",
  9: "Growth never ends. Healing never stops. Expansion continues.",
  10: "Leading herself. Leading her family. Leading her business. Leading her community.",
  11: "Who benefits because she healed? What changes because she said yes?",
  12: "No subtitle. No explanation. Simply… She Is.",
};

export default function SheIsApp() {
  const navigate = useNavigate();
  const [curriculum, setCurriculum] = useState(null);
  const [progress, setProgress] = useState(null);
  const [active, setActive] = useState(1);
  const [tab, setTab] = useState("lesson");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveToast, setSaveToast] = useState(false);
  const [arrivalView, setArrivalView] = useState(null); // null | 0 | 1 | 2
  const [letter, setLetter] = useState(null);
  const [letterPending, setLetterPending] = useState(false);
  const [societyRequested, setSocietyRequested] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return navigate("/coaching/she-is/login");
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get(`/api/coaching/programs/${PROGRAM_SLUG}/curriculum`),
          api.get(`/api/coaching/programs/${PROGRAM_SLUG}/progress`),
        ]);
        setCurriculum(c);
        setProgress(p);
        setActive(p.current_module || 1);
      } catch {
        navigate("/coaching/she-is/login");
      }
    })();
  }, [navigate]);

  const aC = useMemo(
    () => curriculum?.modules.find((m) => m.number === active),
    [curriculum, active]
  );
  const aP = useMemo(
    () => progress?.modules.find((m) => m.module_number === active),
    [progress, active]
  );

  const reload = async () => {
    const p = await api.get(`/api/coaching/programs/${PROGRAM_SLUG}/progress`);
    setProgress(p);
    return p;
  };
  const flashToast = () => {
    setSaveToast(true);
    setTimeout(() => setSaveToast(false), 1200);
  };
  const schedSave = (fn) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await fn();
        await reload();
        flashToast();
      } catch {
        /* transient */
      }
      setSaving(false);
    }, 600);
  };

  function pick(n) {
    const mp = progress?.modules.find((x) => x.module_number === n);
    if (!mp?.unlocked) return;
    setActive(n);
    setTab("lesson");
  }
  function upW(i, v) {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== active) return m;
          const a = [...(m.worksheet_answers || ["", "", "", ""])];
          a[i] = v;
          return { ...m, worksheet_answers: a };
        }),
      };
      const answers = next.modules.find((m) => m.module_number === active).worksheet_answers;
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/worksheet`, { answers })
      );
      return next;
    });
  }
  function upJ(i, v) {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== active) return m;
          const a = [...(m.journal_answers || ["", "", "", "", ""])];
          a[i] = v;
          return { ...m, journal_answers: a };
        }),
      };
      const answers = next.modules.find((m) => m.module_number === active).journal_answers;
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/journal`, { answers })
      );
      return next;
    });
  }
  function upA(v) {
    setProgress((prev) => ({
      ...prev,
      modules: prev.modules.map((m) =>
        m.module_number === active ? { ...m, activity_response: v } : m
      ),
    }));
  }
  async function submit() {
    if (!aP || (aP.activity_response || "").trim().length < 20) return;
    setSubmitting(true);
    try {
      await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/module/${active}/activity`, {
        response: aP.activity_response.trim(),
      });
      await reload();
    } catch {
      /* ignore */
    }
    setSubmitting(false);
  }

  // Declaration (Week 12) state ops
  function upDeclStarter(key, v) {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== 12) return m;
          const dec = m.declaration || { starters: {}, affirmations: [] };
          return {
            ...m,
            declaration: { ...dec, starters: { ...(dec.starters || {}), [key]: v } },
          };
        }),
      };
      const dec = next.modules.find((m) => m.module_number === 12).declaration;
      const affs = dec?.affirmations?.length
        ? dec.affirmations
        : (aC?.declaration?.default_affirmations || []);
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/12/declaration`, {
          starters: dec.starters,
          affirmations: affs,
        })
      );
      return next;
    });
  }
  function upAffirmation(idx, v) {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== 12) return m;
          const dec = m.declaration || { starters: {}, affirmations: [] };
          const affs = [...(dec.affirmations?.length ? dec.affirmations : (aC?.declaration?.default_affirmations || []))];
          affs[idx] = v;
          return { ...m, declaration: { ...dec, affirmations: affs } };
        }),
      };
      const dec = next.modules.find((m) => m.module_number === 12).declaration;
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/12/declaration`, {
          starters: dec.starters || {},
          affirmations: dec.affirmations || [],
        })
      );
      return next;
    });
  }
  function addAffirmation() {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== 12) return m;
          const dec = m.declaration || { starters: {}, affirmations: [] };
          const affs = [...(dec.affirmations?.length ? dec.affirmations : (aC?.declaration?.default_affirmations || []))];
          affs.push("I am…");
          return { ...m, declaration: { ...dec, affirmations: affs } };
        }),
      };
      const dec = next.modules.find((m) => m.module_number === 12).declaration;
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/12/declaration`, {
          starters: dec.starters || {},
          affirmations: dec.affirmations || [],
        })
      );
      return next;
    });
  }
  function removeAffirmation(idx) {
    setProgress((prev) => {
      const next = {
        ...prev,
        modules: prev.modules.map((m) => {
          if (m.module_number !== 12) return m;
          const dec = m.declaration || { starters: {}, affirmations: [] };
          const affs = [...(dec.affirmations?.length ? dec.affirmations : (aC?.declaration?.default_affirmations || []))];
          affs.splice(idx, 1);
          return { ...m, declaration: { ...dec, affirmations: affs } };
        }),
      };
      const dec = next.modules.find((m) => m.module_number === 12).declaration;
      schedSave(() =>
        api.patch(`/api/coaching/programs/${PROGRAM_SLUG}/module/12/declaration`, {
          starters: dec.starters || {},
          affirmations: dec.affirmations || [],
        })
      );
      return next;
    });
  }

  async function enterArrival() {
    setArrivalView(0);
    try {
      const r = await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/future-self-letter`);
      setLetter(r.letter);
    } catch {
      /* letter fetch will retry on stage 1 open */
    }
  }
  async function openLetterStage() {
    setArrivalView(1);
    if (letter?.body) return;
    setLetterPending(true);
    try {
      const r = await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/future-self-letter`);
      setLetter(r.letter);
    } catch {
      /* ignore, empty state renders */
    }
    setLetterPending(false);
  }
  async function requestSociety() {
    try {
      await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/society-request`);
      setSocietyRequested(true);
    } catch {
      /* ignore */
    }
  }

  function logout() {
    [TOKEN_KEY, EMAIL_KEY, NAME_KEY].forEach((k) => localStorage.removeItem(k));
    navigate("/coaching/she-is/login");
  }

  if (!curriculum || !progress) {
    return (
      <div
        data-testid="she-is-loading"
        className="min-h-screen flex items-center justify-center"
        style={{ background: C.white }}
      >
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: C.purple }} />
      </div>
    );
  }

  // Arrival views take over the whole screen
  if (arrivalView !== null) {
    return (
      <ArrivalSequence
        stage={arrivalView}
        setStage={(s) => (s === 1 ? openLetterStage() : setArrivalView(s))}
        letter={letter}
        letterPending={letterPending}
        societyRequested={societyRequested}
        onRequestSociety={requestSociety}
        onExit={() => setArrivalView(null)}
      />
    );
  }

  return (
    <div
      data-testid="she-is-app"
      className="min-h-screen"
      style={{ background: C.white, color: C.black, fontFamily: "'Inter', sans-serif" }}
    >
      <HeroBand progress={progress} onLogout={logout} />
      <JourneyPath curriculum={curriculum} progress={progress} active={active} onPick={pick} />

      <div className="max-w-3xl mx-auto px-6 pt-11 pb-14">
        {aC && aP && (
          <>
            <ModuleHeader active={active} module={aC} total={curriculum.total_modules} />
            <Tabs
              module={aC}
              progress={aP}
              tab={tab}
              onTab={setTab}
              isDeclaration={active === 12}
            />
            <Panel>
              {active === 1 && tab === "lesson" && <ArrivalNote />}

              {tab === "lesson" && (
                <LessonView module={aC} isDeclaration={active === 12} />
              )}

              {tab === "worksheet" && active !== 12 && (
                <WorksheetView module={aC} progress={aP} onChange={upW} saving={saving} />
              )}
              {tab === "journal" && active !== 12 && (
                <JournalView module={aC} progress={aP} onChange={upJ} saving={saving} />
              )}
              {tab === "activity" && active !== 12 && (
                <ActivityView
                  module={aC}
                  progress={aP}
                  onChange={upA}
                  onSubmit={submit}
                  submitting={submitting}
                />
              )}

              {tab === "declaration" && active === 12 && (
                <DeclarationView
                  module={aC}
                  progress={aP}
                  onStarter={upDeclStarter}
                  onAffirmation={upAffirmation}
                  onAddAffirmation={addAffirmation}
                  onRemoveAffirmation={removeAffirmation}
                />
              )}

              <CompleteBar
                aP={aP}
                aC={aC}
                isDeclaration={active === 12}
                isLast={active === curriculum.total_modules}
                onNext={() => {
                  if (active < curriculum.total_modules) {
                    setActive(active + 1);
                    setTab("lesson");
                  } else if (aP.completion_status) {
                    enterArrival();
                  }
                }}
              />
            </Panel>
          </>
        )}
      </div>

      <SaveToast show={saveToast} />
    </div>
  );
}

/* ------------------ Sub-components ------------------ */

function HeroBand({ progress, onLogout }) {
  return (
    <div
      data-testid="she-is-hero"
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(120deg, ${C.purpleDeep} 0%, ${C.purpleBold} 55%, ${C.purple} 100%)`,
        padding: "34px 40px 30px",
      }}
    >
      <div
        style={{
          position: "absolute",
          width: 340,
          height: 340,
          borderRadius: "50%",
          background: `radial-gradient(circle, rgba(127,166,106,0.35), transparent 70%)`,
          top: -160,
          right: -80,
        }}
      />
      <div className="relative max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div
            className="w-11 h-11 rounded-full flex items-center justify-center"
            style={{
              background: "rgba(255,255,255,0.16)",
              border: "1.5px solid rgba(255,255,255,0.5)",
              color: "#fff",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 17,
            }}
          >
            CV
          </div>
          <div>
            <div
              style={{
                fontSize: 10.5,
                color: "rgba(255,255,255,0.65)",
                letterSpacing: "2.8px",
                textTransform: "uppercase",
                fontWeight: 700,
              }}
            >
              KCIS Coaching Door
            </div>
            <div
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 23,
                fontWeight: 700,
                color: "#fff",
              }}
            >
              She Is™
            </div>
            <div
              style={{
                fontSize: 12,
                color: C.green,
                marginTop: 2,
                fontWeight: 700,
                letterSpacing: "0.3px",
              }}
            >
              She is no longer becoming. She has arrived.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div
            className="flex items-center gap-3 rounded-full"
            style={{
              background: "rgba(0,0,0,0.18)",
              padding: "10px 20px",
            }}
            data-testid="hero-progress"
          >
            <div
              className="rounded-md overflow-hidden"
              style={{ width: 150, height: 8, background: "rgba(255,255,255,0.2)" }}
            >
              <div
                data-testid="progress-fill"
                style={{
                  width: `${progress.progress_pct}%`,
                  height: "100%",
                  background: `linear-gradient(90deg, ${C.green}, #A9D18E)`,
                  transition: "width .6s cubic-bezier(.4,0,.2,1)",
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: "#fff", fontWeight: 700 }} data-testid="progress-label">
              {progress.completed_count} / {progress.total_modules} Weeks
            </div>
          </div>
          <button
            onClick={onLogout}
            data-testid="she-is-logout"
            className="text-xs inline-flex items-center gap-1"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function JourneyPath({ curriculum, progress, active, onPick }) {
  return (
    <div
      data-testid="journey-path"
      style={{ background: C.greenSoft, borderBottom: `1px solid ${C.border}`, overflowX: "auto" }}
    >
      <div
        className="max-w-6xl mx-auto flex items-start"
        style={{ padding: "26px 40px", gap: 0, minWidth: "max-content" }}
      >
        {curriculum.modules.map((m, i) => {
          const mp = progress.modules.find((x) => x.module_number === m.number);
          const done = mp?.completion_status;
          const unlocked = mp?.unlocked;
          const isActive = m.number === active;
          const isLast = i === curriculum.modules.length - 1;
          let badgeStyle = {
            background: "#fff",
            color: C.textFaint,
            border: `2px solid ${C.border}`,
          };
          if (done) {
            badgeStyle = {
              background: C.green,
              color: "#fff",
              border: `2px solid ${C.green}`,
              boxShadow: `0 4px 14px rgba(127,166,106,0.5)`,
              cursor: "pointer",
            };
          } else if (unlocked) {
            badgeStyle = {
              background: "#fff",
              color: C.purpleBold,
              border: `2px solid ${C.purpleBold}`,
              boxShadow: `0 0 0 5px rgba(110,55,160,0.12)`,
              cursor: "pointer",
            };
          }
          return (
            <div
              key={m.number}
              data-testid={`path-node-${m.number}`}
              onClick={() => unlocked && onPick(m.number)}
              className="flex flex-col items-center relative shrink-0"
              style={{ width: 96, gap: 10, cursor: unlocked ? "pointer" : "default" }}
            >
              {!isLast && (
                <div
                  style={{
                    position: "absolute",
                    top: 22,
                    left: "calc(50% + 26px)",
                    width: "calc(96px - 26px)",
                    height: 3,
                    background: done ? C.green : C.border,
                    zIndex: 0,
                  }}
                />
              )}
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center relative"
                style={{
                  ...badgeStyle,
                  fontSize: 14,
                  fontWeight: 700,
                  zIndex: 1,
                  outline: isActive ? `3px solid ${C.purpleDeep}` : "none",
                  outlineOffset: 2,
                  transition: "all .4s ease",
                }}
                data-testid={`path-badge-${m.number}`}
              >
                {done ? <Check className="w-5 h-5" /> : unlocked ? m.number : <Lock className="w-3.5 h-3.5" />}
              </div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  textAlign: "center",
                  lineHeight: 1.3,
                  color: unlocked ? C.black : C.textFaint,
                  maxWidth: 92,
                }}
              >
                {m.title.replace("She Is ", "")}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModuleHeader({ active, module: m, total }) {
  return (
    <div className="mb-8" data-testid="module-header" style={{ animation: "riseIn .55s ease both" }}>
      <div
        className="inline-flex items-center gap-2 rounded-full mb-4"
        style={{
          background: C.purpleDeep,
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: "1.2px",
          textTransform: "uppercase",
          padding: "7px 16px",
        }}
      >
        Week {active} of {total}
        {active === 12 && " · Final Week"}
      </div>
      <h1
        className="text-4xl leading-tight mb-3"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800 }}
        data-testid="module-title"
      >
        {m.title}
      </h1>
      <p
        className="mb-3 italic"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 16.5,
          color: C.purpleBold,
        }}
      >
        {WEEKLY_SUBTITLES[active]}
      </p>
      <div style={{ fontSize: 15.5, color: C.textDim, maxWidth: 640, lineHeight: 1.75 }}>
        {m.objective}
      </div>
    </div>
  );
}

function Tabs({ module: m, progress: p, tab, onTab, isDeclaration }) {
  const wsOk =
    Array.isArray(p.worksheet_answers) &&
    p.worksheet_answers.length === 4 &&
    p.worksheet_answers.every((x) => (x || "").trim().length >= 5);
  const jOk =
    Array.isArray(p.journal_answers) &&
    p.journal_answers.length === 5 &&
    p.journal_answers.every((x) => (x || "").trim().length >= 10);
  const aOk = (p.activity_response || "").trim().length >= 20;
  const declOk = isDeclaration && p.completion_status;

  const tabs = isDeclaration
    ? [
        ["lesson", "Lesson", true],
        ["declaration", "Your Declaration", declOk],
      ]
    : [
        ["lesson", "Lesson", true],
        ["worksheet", "Worksheet", wsOk],
        ["journal", "Journal", jOk],
        ["activity", "Activity", aOk],
      ];
  return (
    <div className="flex gap-2 mb-7 flex-wrap" data-testid="she-is-tabs">
      {tabs.map(([k, l, d]) => {
        const a = tab === k;
        return (
          <button
            key={k}
            onClick={() => onTab(k)}
            data-testid={`tab-${k}`}
            className="inline-flex items-center gap-2"
            style={{
              padding: "11px 20px",
              fontSize: 13,
              fontWeight: 700,
              color: a ? "#fff" : C.textDim,
              borderRadius: 999,
              background: a
                ? `linear-gradient(120deg, ${C.purpleBold}, ${C.purpleDeep})`
                : C.greenSoft,
              transition: "all .3s ease",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: d ? C.green : a ? "rgba(255,255,255,0.4)" : C.silver }}
            />
            {l}
          </button>
        );
      })}
    </div>
  );
}

function Panel({ children }) {
  return (
    <div
      data-testid="she-is-panel"
      style={{
        background: "#fff",
        border: `1px solid ${C.border}`,
        borderRadius: 28,
        padding: 42,
        boxShadow: C.shadow,
        animation: "riseIn .55s ease both",
      }}
    >
      {children}
    </div>
  );
}

function ArrivalNote() {
  return (
    <div
      className="mb-7 rounded-2xl"
      data-testid="arrival-note"
      style={{
        background: `linear-gradient(120deg, ${C.purpleDeep}, ${C.purpleBold})`,
        color: "#fff",
        padding: "26px 28px",
      }}
    >
      <h3 className="mb-4 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
        What This 12 Weeks Moves You Through
      </h3>
      <div className="flex flex-wrap gap-7">
        <div className="flex-1 min-w-[200px]">
          <div
            style={{
              fontSize: 10.5,
              letterSpacing: "1.8px",
              textTransform: "uppercase",
              fontWeight: 800,
              color: "rgba(255,255,255,0.55)",
              marginBottom: 10,
            }}
          >
            Before
          </div>
          <ul
            className="pl-4"
            style={{ color: "rgba(255,255,255,0.65)", fontSize: 13.5, lineHeight: 1.9 }}
          >
            <li>Self-doubt</li>
            <li>Playing small</li>
            <li>Waiting for permission</li>
            <li>Seeking validation</li>
            <li>Fear of visibility</li>
            <li>Fear of success</li>
            <li>Fear of being fully seen</li>
          </ul>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div
            style={{
              fontSize: 10.5,
              letterSpacing: "1.8px",
              textTransform: "uppercase",
              fontWeight: 800,
              color: C.green,
              marginBottom: 10,
            }}
          >
            After
          </div>
          <ul
            className="pl-4"
            style={{ color: "#fff", fontSize: 13.5, lineHeight: 1.9, fontWeight: 700 }}
          >
            <li>Self-trust</li>
            <li>Confidence</li>
            <li>Peace</li>
            <li>Purpose</li>
            <li>Leadership</li>
            <li>Healthy relationships</li>
            <li>Authenticity</li>
            <li>Freedom</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function LessonView({ module: m, isDeclaration }) {
  return (
    <div data-testid="lesson-view">
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 10,
        }}
      >
        {isDeclaration ? "This Week" : "This Week's Reflection"}
      </p>
      <p
        className="mb-7 rounded-2xl"
        data-testid="lesson-quote"
        style={{
          fontFamily: "'Playfair Display', serif",
          fontStyle: "italic",
          fontSize: 22,
          lineHeight: 1.6,
          color: C.purpleDeep,
          background: C.greenSoft,
          padding: "26px 30px",
        }}
      >
        “{m.reflection_quote}”
      </p>
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 10,
        }}
      >
        Coaching Guidance
      </p>
      <p
        style={{ fontSize: 15.5, lineHeight: 1.85, color: "#332C3A", maxWidth: 640 }}
        data-testid="lesson-body"
      >
        {m.coaching_body}
      </p>
      <p className="mt-4 text-xs" style={{ color: C.textFaint }}>
        {isDeclaration
          ? "When you're ready, move to the Your Declaration tab to write your She Is Statement™."
          : "Read through this lesson, then move to the Worksheet, Journal, and Activity tabs above to complete this week."}
      </p>
    </div>
  );
}

function WorksheetView({ module: m, progress: p, onChange, saving }) {
  return (
    <div data-testid="worksheet-view">
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        Reflection Worksheet
      </p>
      {m.worksheet_prompts.map((q, i) => (
        <div key={i} className="mb-5">
          <label className="block text-sm font-semibold mb-2">
            {i + 1}. {q}
          </label>
          <textarea
            data-testid={`worksheet-input-${i}`}
            value={(p.worksheet_answers || [])[i] || ""}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder="Write your reflection here..."
            className="w-full focus:outline-none"
            style={{
              minHeight: 82,
              border: `2px solid ${C.border}`,
              borderRadius: 10,
              padding: "13px 15px",
              fontSize: 14,
              lineHeight: 1.65,
              background: "#FBFAFC",
              resize: "vertical",
            }}
          />
        </div>
      ))}
      <div className="text-xs" style={{ color: C.textFaint }}>
        {saving ? "Saving…" : "Saved automatically as you write."}
      </div>
    </div>
  );
}

function JournalView({ module: m, progress: p, onChange, saving }) {
  return (
    <div data-testid="journal-view">
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        Daily Journal Prompts
      </p>
      {m.journal_prompts.map((q, i) => (
        <div
          key={i}
          className="mb-4"
          data-testid={`journal-card-${i}`}
          style={{
            border: `2px solid ${C.greenSoft}`,
            borderRadius: 18,
            padding: "20px 22px",
            background: "#fff",
          }}
        >
          <span
            className="inline-block mb-2 rounded-full"
            style={{
              fontSize: 10.5,
              letterSpacing: "1.8px",
              textTransform: "uppercase",
              color: "#fff",
              background: C.greenBold,
              fontWeight: 800,
              padding: "4px 12px",
            }}
          >
            Day {i + 1}
          </span>
          <div className="text-sm font-semibold mb-3">{q}</div>
          <textarea
            data-testid={`journal-input-${i}`}
            value={(p.journal_answers || [])[i] || ""}
            onChange={(e) => onChange(i, e.target.value)}
            placeholder="Begin writing..."
            className="w-full focus:outline-none"
            style={{
              minHeight: 82,
              border: `2px solid ${C.border}`,
              borderRadius: 10,
              padding: "13px 15px",
              fontSize: 14,
              lineHeight: 1.65,
              background: "#FBFAFC",
              resize: "vertical",
            }}
          />
        </div>
      ))}
      <div className="text-xs" style={{ color: C.textFaint }}>
        {saving ? "Saving…" : "Saved automatically as you write."}
      </div>
    </div>
  );
}

function ActivityView({ module: m, progress: p, onChange, onSubmit, submitting }) {
  const submitted = (p.activity_response || "").trim().length >= 20;
  const fb = p.coach_override_feedback || p.ai_feedback;

  return (
    <div data-testid="activity-view">
      <div
        className="rounded-2xl"
        style={{
          border: `2px solid ${C.purpleBold}`,
          background: `linear-gradient(135deg, #fff, ${C.greenSoft})`,
          padding: 28,
          marginBottom: 4,
        }}
      >
        <p
          style={{
            fontSize: 11,
            letterSpacing: "2.2px",
            textTransform: "uppercase",
            color: C.purpleBold,
            fontWeight: 800,
            marginBottom: 10,
          }}
        >
          This Week's Activity
        </p>
        <p
          style={{ fontSize: 15.5, lineHeight: 1.8, marginBottom: 20 }}
          data-testid="activity-prompt"
        >
          {m.activity_prompt}
        </p>
        <textarea
          data-testid="activity-input"
          value={p.activity_response || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Write what you did and what came up for you..."
          className="w-full focus:outline-none"
          style={{
            minHeight: 140,
            border: `2px solid ${C.border}`,
            borderRadius: 10,
            padding: "15px 17px",
            fontSize: 14,
            lineHeight: 1.7,
            background: "#fff",
            resize: "vertical",
          }}
        />
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <button
            onClick={onSubmit}
            disabled={submitting || (p.activity_response || "").trim().length < 20}
            data-testid="submit-activity-btn"
            className="rounded-full text-white disabled:opacity-40 inline-flex items-center gap-2"
            style={{
              background: `linear-gradient(120deg, ${C.purpleBold}, ${C.purpleDeep})`,
              boxShadow: `0 10px 24px rgba(91,44,131,0.35)`,
              padding: "15px 30px",
              fontSize: 13.5,
              fontWeight: 800,
              letterSpacing: "0.3px",
              transition: "all .3s ease",
            }}
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Reflecting…
              </>
            ) : submitted ? (
              "Update My Response"
            ) : (
              "Submit for Coaching Feedback"
            )}
          </button>
          <span style={{ fontSize: 12, color: C.textFaint }}>
            {submitted
              ? "Saved — Dr. Christy can review this in your progress log."
              : "Requires at least a few sentences before submitting."}
          </span>
        </div>
      </div>

      {fb && <FeedbackCard fb={fb} source={p.ai_feedback?.source} onRegen={onSubmit} />}
    </div>
  );
}

function FeedbackCard({ fb, source, onRegen }) {
  return (
    <div
      className="mt-6 rounded-2xl"
      data-testid="ai-feedback-card"
      style={{
        border: `2px solid ${C.greenBold}`,
        background: "#fff",
        padding: "26px 28px",
        animation: "riseIn .5s ease both",
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
          style={{
            background: `linear-gradient(135deg, ${C.purpleBold}, ${C.purpleDeep})`,
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
          }}
        >
          CV
        </div>
        <div>
          <div className="text-xs font-extrabold" style={{ color: C.purpleBold, letterSpacing: "0.3px" }}>
            Coaching Reflection
          </div>
          <div className="text-[11px]" style={{ color: C.textFaint }}>
            {source === "ai"
              ? "Generated in Dr. Christy's coaching voice"
              : "From Dr. Christy's coaching library"}
          </div>
        </div>
      </div>
      {[
        ["Reflection", fb.reflection, C.purpleBold],
        ["Observation", fb.observation, C.purpleBold],
        ["Reframe", fb.reframe, C.purpleBold],
        ["Encouragement", fb.encouragement, C.purpleBold],
      ].map(([label, txt, col]) => (
        <div key={label} className="mb-3">
          <p
            style={{
              fontSize: 10.5,
              letterSpacing: "2px",
              textTransform: "uppercase",
              fontWeight: 800,
              color: col,
              marginBottom: 6,
              marginTop: 15,
            }}
          >
            {label}
          </p>
          <p style={{ fontSize: 14.5, lineHeight: 1.75, color: "#332C3A", margin: 0 }}>{txt}</p>
        </div>
      ))}
      <p
        style={{
          fontSize: 10.5,
          letterSpacing: "2px",
          textTransform: "uppercase",
          fontWeight: 800,
          color: C.greenBold,
          marginBottom: 6,
          marginTop: 15,
        }}
      >
        Next Action
      </p>
      <p style={{ fontSize: 14.5, lineHeight: 1.75, color: "#332C3A", margin: 0 }}>
        {fb.next_action}
      </p>
      <div className="mt-4">
        <button
          onClick={onRegen}
          data-testid="regen-feedback-btn"
          className="rounded-full inline-flex items-center gap-2"
          style={{
            background: "#fff",
            border: `2px solid ${C.purpleBold}`,
            color: C.purpleBold,
            padding: "10px 20px",
            fontSize: 12.5,
            fontWeight: 800,
          }}
        >
          <Sparkles className="w-3 h-3" /> Get a fresh reflection
        </button>
      </div>
    </div>
  );
}

function DeclarationView({
  module: m,
  progress: p,
  onStarter,
  onAffirmation,
  onAddAffirmation,
  onRemoveAffirmation,
}) {
  const cfg = m.declaration;
  const dec = p.declaration || { starters: {}, affirmations: [] };
  const affs =
    dec.affirmations && dec.affirmations.length ? dec.affirmations : cfg.default_affirmations;
  return (
    <div data-testid="declaration-view">
      <p
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        Build Your She Is Statement™
      </p>

      {cfg.sentence_starters.map((s) => (
        <div key={s.key} className="mb-6">
          <label
            className="block mb-2 italic"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 17,
              color: C.purpleBold,
            }}
          >
            {s.prefix}
          </label>
          <textarea
            data-testid={`decl-${s.key}`}
            value={(dec.starters || {})[s.key] || ""}
            onChange={(e) => onStarter(s.key, e.target.value)}
            placeholder={s.placeholder}
            className="w-full focus:outline-none"
            style={{
              minHeight: 70,
              border: `2px solid ${C.border}`,
              borderRadius: 10,
              padding: "14px 16px",
              fontSize: 15,
              lineHeight: 1.7,
              background: "#FBFAFC",
              resize: "vertical",
            }}
          />
        </div>
      ))}

      <p
        className="mt-2"
        style={{
          fontSize: 11,
          letterSpacing: "2.2px",
          textTransform: "uppercase",
          color: C.greenBold,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        Your Affirmations
      </p>
      <div className="flex flex-col gap-3 mb-3">
        {affs.map((a, idx) => (
          <div key={idx} className="flex items-center gap-3" data-testid={`affirm-row-${idx}`}>
            <input
              value={a}
              onChange={(e) => onAffirmation(idx, e.target.value)}
              className="flex-1 focus:outline-none"
              style={{
                border: `2px solid ${C.greenBold}`,
                borderRadius: 999,
                padding: "11px 18px",
                fontSize: 14.5,
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                color: C.black,
                background: "#fff",
              }}
            />
            <button
              onClick={() => onRemoveAffirmation(idx)}
              data-testid={`affirm-remove-${idx}`}
              className="w-7 h-7 rounded-full"
              style={{ background: C.border, color: C.textFaint, fontSize: 13 }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={onAddAffirmation}
        data-testid="affirm-add"
        className="rounded-full text-white"
        style={{
          background: C.greenBold,
          padding: "10px 18px",
          fontSize: 12.5,
          fontWeight: 800,
        }}
      >
        + Add another "I am…"
      </button>

      <DeclarationPreview cfg={cfg} dec={{ ...dec, affirmations: affs }} />
    </div>
  );
}

function DeclarationPreview({ cfg, dec }) {
  const startersLines = cfg.sentence_starters
    .map((s) => ({ prefix: s.prefix, val: (dec.starters || {})[s.key] || "" }))
    .filter((l) => l.val.trim());
  const affirms = (dec.affirmations || []).filter((a) => (a || "").trim());
  if (!startersLines.length && !affirms.length) return null;
  return (
    <div
      className="mt-8"
      data-testid="decl-preview"
      style={{
        padding: "32px 34px",
        borderRadius: 18,
        background: `linear-gradient(150deg, ${C.purpleDeep}, ${C.purpleBold})`,
      }}
    >
      <div
        style={{
          fontSize: 10.5,
          letterSpacing: "2.5px",
          textTransform: "uppercase",
          color: C.green,
          fontWeight: 800,
          marginBottom: 14,
        }}
      >
        Your She Is Statement™
      </div>
      {startersLines.map((l, i) => (
        <p
          key={i}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 17,
            fontStyle: "italic",
            lineHeight: 1.9,
            color: "#fff",
            margin: "0 0 6px",
          }}
        >
          {l.prefix} {l.val}
        </p>
      ))}
      {affirms.map((a, i) => (
        <p
          key={`aff-${i}`}
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 17,
            fontStyle: "italic",
            lineHeight: 1.9,
            color: "#fff",
            margin: "0 0 6px",
          }}
        >
          {a}
        </p>
      ))}
    </div>
  );
}

function CompleteBar({ aP, aC, isDeclaration, isLast, onNext }) {
  const complete = aP.completion_status;
  const status = complete
    ? isLast
      ? "Your She Is Statement is complete. Continue to your arrival."
      : "All requirements met. You may continue."
    : isDeclaration
    ? "Complete all four declaration fields to finish your She Is Statement."
    : "Complete the worksheet, all 5 journal prompts, and the activity to unlock the next week.";
  const label = isLast ? "Complete Your Arrival →" : "Continue →";
  return (
    <div
      className="flex items-center justify-between mt-8 pt-7 flex-wrap gap-3"
      style={{ borderTop: `2px dashed ${C.border}` }}
      data-testid="complete-bar"
    >
      <div className="text-sm" style={{ color: C.textDim }}>
        {complete ? <strong style={{ color: C.greenBold }}>{status}</strong> : status}
      </div>
      <button
        onClick={onNext}
        disabled={!complete}
        data-testid="continue-btn"
        className="rounded-full text-white disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          background: complete
            ? `linear-gradient(120deg, ${C.purpleBold}, ${C.purpleDeep})`
            : C.border,
          color: complete ? "#fff" : C.textFaint,
          padding: "15px 30px",
          fontSize: 13.5,
          fontWeight: 800,
          letterSpacing: "0.3px",
          boxShadow: complete ? `0 10px 24px rgba(91,44,131,0.35)` : "none",
          transition: "all .3s ease",
        }}
      >
        {label}
      </button>
    </div>
  );
}

function SaveToast({ show }) {
  return (
    <div
      data-testid="save-toast"
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        background: C.purpleDeep,
        color: "#fff",
        padding: "12px 20px",
        borderRadius: 999,
        fontSize: 12.5,
        opacity: show ? 1 : 0,
        transform: show ? "translateY(0)" : "translateY(10px)",
        transition: ".5s ease",
        pointerEvents: "none",
        zIndex: 100,
      }}
    >
      Saved
    </div>
  );
}

/* ------------------ Arrival Sequence ------------------ */

function ArrivalSequence({
  stage,
  setStage,
  letter,
  letterPending,
  societyRequested,
  onRequestSociety,
  onExit,
}) {
  return (
    <div
      data-testid="arrival-sequence"
      className="min-h-screen"
      style={{ background: C.white, fontFamily: "'Inter', sans-serif" }}
    >
      <div className="max-w-3xl mx-auto pt-14 pb-16 px-6">
        <div className="flex justify-center gap-3 mb-8" data-testid="arrival-stepper">
          {[0, 1, 2].map((s) => (
            <div
              key={s}
              className="w-2.5 h-2.5 rounded-full"
              style={{ background: s === stage ? C.greenBold : C.border }}
              data-testid={`stage-dot-${s}`}
            />
          ))}
        </div>
        {stage === 0 && <DeclarationCertificate onNext={() => setStage(1)} onExit={onExit} />}
        {stage === 1 && (
          <FutureSelfLetter
            letter={letter}
            pending={letterPending}
            onBack={() => setStage(0)}
            onNext={() => setStage(2)}
          />
        )}
        {stage === 2 && (
          <SocietyInvitation
            requested={societyRequested}
            onRequest={onRequestSociety}
            onBack={() => setStage(1)}
            onExit={onExit}
          />
        )}
      </div>
    </div>
  );
}

function DeclarationCertificate({ onNext, onExit }) {
  const [name, setName] = useState(localStorage.getItem(NAME_KEY) || "");
  async function confirmAndPrint() {
    if (!name.trim()) return;
    try {
      await api.post(`/api/coaching/programs/${PROGRAM_SLUG}/certificate/confirm-name`, {
        display_name: name.trim(),
      });
      localStorage.setItem(NAME_KEY, name.trim());
      setTimeout(() => window.print(), 400);
    } catch {
      window.print();
    }
  }
  return (
    <div data-testid="declaration-cert">
      <button
        onClick={onExit}
        className="text-xs mb-6"
        style={{ color: C.textFaint }}
        data-testid="cert-back-btn"
      >
        ← Back to program
      </button>
      <div
        className="text-center relative overflow-hidden"
        style={{
          background: `linear-gradient(150deg, ${C.purpleDeep}, ${C.purpleBold})`,
          borderRadius: 28,
          padding: "66px 46px",
          boxShadow: "0 30px 70px rgba(91,44,131,0.3)",
          color: "#fff",
        }}
        data-testid="she-is-certificate-doc"
      >
        <div
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(127,166,106,0.3), transparent 70%)`,
            top: -140,
            right: -60,
          }}
        />
        <div className="relative">
          <div
            className="w-[74px] h-[74px] rounded-full mx-auto flex items-center justify-center"
            style={{
              background: C.green,
              color: "#fff",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 25,
              marginBottom: 24,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            }}
          >
            CV
          </div>
          <div style={{ fontSize: 11, letterSpacing: "3px", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}>
            This Certifies That
          </div>
          <div
            className="my-3"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 36,
              fontWeight: 800,
              color: "#fff",
            }}
            data-testid="cert-display-name"
          >
            {name || "Your Name"}
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.8)" }}>
            has completed the full 12-week arrival
          </div>
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 27,
              color: C.green,
              margin: "12px 0 22px",
              fontWeight: 800,
            }}
          >
            She Is™
          </div>
          <div style={{ fontSize: 12.5, color: "rgba(255,255,255,0.75)" }}>
            She Is Declaration Certificate
          </div>
          <div style={{ height: 1, background: "rgba(255,255,255,0.2)", margin: "30px 0" }} />
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 17, fontWeight: 700 }}>
            Dr. Christy Vonta
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 3 }}>
            Christy Vonta Coaching &amp; Consulting · KCIS Coaching Door
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-center gap-3 flex-wrap print:hidden">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Type your full name"
          data-testid="cert-name-input"
          className="focus:outline-none"
          style={{
            padding: "13px 18px",
            border: `2px solid ${C.border}`,
            borderRadius: 999,
            fontSize: 14,
            minWidth: 240,
          }}
        />
        <button
          onClick={confirmAndPrint}
          disabled={!name.trim()}
          data-testid="cert-print-btn"
          className="rounded-full text-white disabled:opacity-40 inline-flex items-center gap-2"
          style={{
            background: `linear-gradient(120deg, ${C.purpleBold}, ${C.purpleDeep})`,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
            boxShadow: "0 8px 22px rgba(91,44,131,0.35)",
          }}
        >
          <Printer className="w-4 h-4" /> Download Certificate
        </button>
      </div>
      <div className="mt-4 flex justify-center print:hidden">
        <button
          onClick={onNext}
          data-testid="continue-to-letter-btn"
          className="rounded-full inline-flex items-center gap-2"
          style={{
            background: "#fff",
            border: `2px solid ${C.purpleBold}`,
            color: C.purpleBold,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
          }}
        >
          Continue to Your Letter <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function FutureSelfLetter({ letter, pending, onBack, onNext }) {
  return (
    <div data-testid="future-self-letter">
      {pending ? (
        <div className="flex justify-center items-center gap-3 py-14" style={{ color: C.textDim }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: C.purpleBold }} />
          <span>Writing your letter from the woman you've become...</span>
        </div>
      ) : letter?.body ? (
        <div
          className="rounded-3xl"
          style={{
            background: "#fff",
            border: `2px solid ${C.greenBold}`,
            padding: "48px 44px",
            boxShadow: "0 20px 60px rgba(127,166,106,0.15)",
          }}
        >
          <div className="text-center mb-6">
            <span
              className="block mb-2"
              style={{
                fontSize: 11,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
                color: C.greenBold,
                fontWeight: 800,
              }}
            >
              A Letter From
            </span>
            <h2
              className="text-2xl m-0"
              style={{
                fontFamily: "'Playfair Display', serif",
                color: C.purpleDeep,
              }}
            >
              The Woman You've Become
            </h2>
          </div>
          <div
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 16.5,
              lineHeight: 2,
              color: "#332C3A",
              whiteSpace: "pre-wrap",
            }}
            data-testid="letter-body"
          >
            {letter.body}
          </div>
        </div>
      ) : (
        <div className="text-center py-10" style={{ color: C.textDim }}>
          Your letter is being prepared. Please give it a moment...
        </div>
      )}

      <div className="mt-6 flex justify-center gap-3 flex-wrap">
        <button
          onClick={onBack}
          data-testid="letter-back-btn"
          className="rounded-full inline-flex items-center gap-2"
          style={{
            background: "#fff",
            border: `2px solid ${C.purpleBold}`,
            color: C.purpleBold,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
          }}
        >
          ← Back to Certificate
        </button>
        <button
          onClick={onNext}
          disabled={pending}
          data-testid="letter-next-btn"
          className="rounded-full text-white disabled:opacity-40 inline-flex items-center gap-2"
          style={{
            background: `linear-gradient(120deg, ${C.purpleBold}, ${C.purpleDeep})`,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
            boxShadow: "0 8px 22px rgba(91,44,131,0.35)",
          }}
        >
          Continue <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function SocietyInvitation({ requested, onRequest, onBack, onExit }) {
  return (
    <div data-testid="society-invitation">
      <div
        className="text-center rounded-3xl"
        style={{
          background: `linear-gradient(150deg, ${C.greenBold}, ${C.green})`,
          padding: "52px 44px",
          color: "#fff",
          boxShadow: "0 24px 60px rgba(127,166,106,0.35)",
        }}
      >
        <h2
          className="mb-4"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 28,
            margin: "0 0 14px",
          }}
        >
          Welcome to the She Is Society™
        </h2>
        <p
          className="mx-auto mb-6"
          style={{ fontSize: 15, lineHeight: 1.85, opacity: 0.95, maxWidth: 520 }}
        >
          This isn't the end of your program. It's the beginning of your community — an ongoing
          circle of women who have done this work and are living it out loud, together. You've earned
          your place here.
        </p>
        {requested ? (
          <div
            data-testid="society-confirmed"
            className="mt-4"
            style={{ fontSize: 13.5, opacity: 0.95 }}
          >
            Your invitation request has been noted. Dr. Christy's team will follow up with next steps
            for the She Is Society.
          </div>
        ) : (
          <button
            onClick={onRequest}
            data-testid="society-request-btn"
            className="rounded-full inline-flex items-center gap-2"
            style={{
              background: "#fff",
              color: C.purpleDeep,
              padding: "13px 26px",
              fontSize: 13.5,
              fontWeight: 800,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
          >
            I'm Ready — Send My Invitation Request
          </button>
        )}
      </div>
      <div className="mt-6 flex justify-center gap-3 flex-wrap">
        <button
          onClick={onBack}
          data-testid="society-back-btn"
          className="rounded-full inline-flex items-center gap-2"
          style={{
            background: "#fff",
            border: `2px solid ${C.purpleBold}`,
            color: C.purpleBold,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
          }}
        >
          ← Back to Your Letter
        </button>
        <Link to="/coaching/trilogy/certificate" data-testid="society-trilogy-btn">
          <button
            className="rounded-full inline-flex items-center gap-2 text-white"
            style={{
              background: `linear-gradient(120deg, ${C.purpleBold}, ${C.green})`,
              padding: "13px 24px",
              fontSize: 13.5,
              fontWeight: 800,
              boxShadow: "0 8px 22px rgba(127,166,106,0.4)",
            }}
          >
            View my Master Trilogy Certificate <ChevronRight className="w-4 h-4" />
          </button>
        </Link>
        <button
          onClick={onExit}
          data-testid="society-review-btn"
          className="rounded-full inline-flex items-center gap-2"
          style={{
            background: "transparent",
            border: `2px solid ${C.textFaint}`,
            color: C.textFaint,
            padding: "13px 24px",
            fontSize: 13.5,
            fontWeight: 800,
          }}
        >
          Review My Journey
        </button>
      </div>
    </div>
  );
}
